import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { instanceOfDOMNodeOrNull } from 'bloko/common/helpers/customPropTypes';

import {
    LAYERS,
    LAYER_CLASSES,
    FLEXIBLE_CLASS,
    ANIMATION_TIMEOUT_MS,
    DOWN_PLACEMENTS,
    PLACEMENT_DOWN_SEQUENCE,
    setupWidth,
} from './constants';
import { updatePosition, setInitialCSSMetrics } from './common';

export default class DropBase extends PureComponent {
    state = { show: this.props.show, animate: false, isFirstRender: true };
    dropRef = React.createRef();
    arrowRef = React.createRef();
    clickedInsideTheDrop = false;
    static propTypes = {
        /** Базовые класс для down / menu / info */
        baseClassNames: PropTypes.array.isRequired,
        /** Тип компонента */
        behavior: PropTypes.shape({
            showArrow: PropTypes.bool.isRequired,
            arrowSize: PropTypes.number.isRequired,
            setupFullWidthOnXS: PropTypes.bool.isRequired,
            placementOffset: PropTypes.number.isRequired,
            alignToActivatorBorders: PropTypes.bool.isRequired,
        }).isRequired,

        /** Флаг — закрывать ли компонент по клику извне */
        closeByClickOutside: PropTypes.bool,
        /** Предпочтительное положение компонента */
        placement: PropTypes.oneOf(Object.values(DOWN_PLACEMENTS)),
        /** Выпадать только в заданном направлении, не обрабатывать другие кейсы */
        onlySetPlacement: PropTypes.bool,
        /** Class z-index-а компонента */
        layer: PropTypes.oneOf(Object.values(LAYERS)),
        /** DOM нода хоста в рамках которого нужно рендерить компонент, по дефолту рендер будет в родителе компонента.*/
        host: instanceOfDOMNodeOrNull(),
        /** Флаг отвечает за показ компонента, `true` - показать, `false` - скрыть */
        show: PropTypes.bool,
        /** Элемент инициатор, относительно которого показывать всплывающую подсказку */
        children: PropTypes.node,
        /** Метод-рендер контента */
        render: PropTypes.func.isRequired,
        /** Data-qa компонента */
        dataQa: PropTypes.string,
        /** Колбек вызываемый при закрытии компонента. Срабатывает в случае клика на крестик или клика вне компонента */
        onClose: PropTypes.func,
        /** Метка, использовать ли тянущийся контент.
         * Тянущийся компоенент будет растягивать по контенту, ограничиваясь только размерами экрана */
        flexible: PropTypes.bool,
    };

    componentDidMount() {
        this.activatorRef = ReactDOM.findDOMNode(this);

        this.setState({
            isFirstRender: false,
        });

        if (this.state.show) {
            this.setupDropElement();
        }

        // Слушаем клик на активаторе явно. Делаем это по следующим причинам:
        // 1) Установка флаг для превента при смене show приводит к тому,
        // что если компонент появляется программно, то клик будет проигнорирован.
        // 2) Если ставить window.addEL('click') после того, как компонент отрисуется, то событие клика на элемент будет
        // всплывать и в итоге дроп компонент закроется. Это происходит потому что:
        // 2.1 кликнули на элемент активатор
        // 2.2 появилось событие click
        // 2.3 поймали событие клика, поменяли show в true
        // 2.4 дроп компонент отрендерился, вызвались все нужные layout, подписались на window.addEL('click')
        // 2.5 событие пошло всплывать, наша подписка скрыла компонент
        this.activatorRef.addEventListener && this.activatorRef.addEventListener('click', this.preventCloseCallback);
        window.addEventListener('resize', this.handleResize);
        if (this.props.closeByClickOutside) {
            window.addEventListener('click', this.callOnClose);
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.show && !prevProps.show) {
            this.setState({ show: true }, () => {
                this.setupDropElement();
            });
        }
        if (!this.props.show && prevProps.show) {
            this.setState({ animate: true, show: false }, () => {
                this.placement = null;
            });
        }
        this.updateDropPosition();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('click', this.callOnClose);
        this.activatorRef.removeEventListener('click', this.preventCloseCallback);
    }

    handleResize = () => {
        this.placement = null;
        this.setupWidth();
        this.updateDropPosition();
    };

    setupDropElement() {
        setInitialCSSMetrics(this.dropRef.current);
        this.setupWidth();
        this.updateDropPosition();
    }

    setupWidth = () => {
        if (!this.props.behavior.setupFullWidthOnXS) {
            return;
        }

        if (!this.state.show || !this.dropRef.current) {
            return;
        }

        setupWidth(this.dropRef.current, this.getHost());
    };

    getHost() {
        return this.props.host || document.body;
    }

    callOnClose = () => {
        if (!this.props.show) {
            this.clickedInsideTheDrop = false;
            return;
        }

        if (this.state.show && this.props.show) {
            if (this.clickedInsideTheDrop) {
                this.clickedInsideTheDrop = false;
                return;
            }
            this.props.onClose();
        }
    };

    preventCloseCallback = () => {
        this.clickedInsideTheDrop = true;
    };

    updateDropPosition = () => {
        if (!this.state.show || !this.dropRef.current) {
            return;
        }

        let placementSequence = PLACEMENT_DOWN_SEQUENCE;
        if (this.props.onlySetPlacement) {
            placementSequence = [this.props.placement];
        }

        const position = updatePosition({
            behavior: this.props.behavior,
            placementSequence,
            activatorElement: this.activatorRef,
            dropElement: this.dropRef.current,
            host: this.getHost(),
            placement: this.placement || this.props.placement,
            classNames: ['bloko-drop', ...this.props.baseClassNames, LAYER_CLASSES[this.props.layer], FLEXIBLE_CLASS],
            arrow: this.arrowRef.current,
            arrowSize: this.props.behavior.arrowSize,
        });

        this.placement = position.placement;
    };

    renderBody() {
        if (!this.state.show && !this.state.animate) {
            return null;
        }

        return (
            <Fragment>
                {this.props.render()}
                {this.props.behavior.showArrow && <div className="bloko-drop__arrow" ref={this.arrowRef} />}
            </Fragment>
        );
    }

    renderDrop() {
        const {
            children,
            placement,
            host,
            show,
            layer,
            render,
            onClose,
            onlySetPlacement,
            closeByClickOutside,
            dataQa,
            flexible,
            baseClassNames,
            behavior,
            ...other
        } = this.props;

        const cssClasses = ['bloko-drop', ...baseClassNames, LAYER_CLASSES[layer]];
        if (flexible) {
            cssClasses.push(FLEXIBLE_CLASS);
        }

        return (
            <CSSTransition
                in={this.state.show}
                timeout={{
                    enter: ANIMATION_TIMEOUT_MS,
                    exit: 0,
                }}
                onExited={() => {
                    this.setState({ animate: false });
                }}
                unmountOnExit
                classNames={{
                    enterActive: 'bloko-drop_active-enter',
                    enterDone: 'bloko-drop_done-enter',
                }}
            >
                <div
                    {...other}
                    data-qa={dataQa}
                    className={cssClasses.join(' ')}
                    ref={this.dropRef}
                    onClick={this.preventCloseCallback}
                >
                    {this.renderBody()}
                </div>
            </CSSTransition>
        );
    }

    render() {
        if (this.state.isFirstRender) {
            return this.props.children;
        }

        return (
            <Fragment>
                {this.props.children}
                {ReactDOM.createPortal(this.renderDrop(), this.getHost())}
            </Fragment>
        );
    }
}
